// Shared MUI primitives for API Security v2 pages — replaces per-page
// design-pack copies of Card, PageHead, PageTabs, Btn, Pill, Hero.
import React from "react";
import {
  Box, Stack, Typography, Chip, Tabs, Tab,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";

import colors from "../../../../../../utils/colors";
import LayerdIcon from "../../../../../common/LayerdIcon";
import {
  asecSectionCardSx,
  asecContentCardSx,
  asecSectionTitleSx,
  asecTabsSx,
  EVENT_FILTER_BAR_SX,
  asecSeverityConfig,
  ASEC_MODE_TONE_COLORS,
  asecModeSegmentedTrackSx,
  asecTypography,
  asecOverviewMetricCardSx,
} from "../asecUi";
import WAFOverviewMetricCard, {
  wafOverviewGridSxForCount,
  wafOverviewSubSx,
  wafOverviewStatNumberSx,
  wafOverviewStatUnitSx,
  WAFOverviewIconChip,
  WAFOverviewStatNumber,
} from "../../web_application/components/WAFOverviewMetricCard";

const ACCENT = colors.primary[300];
const GREEN = colors.green[300];
const MODE_SEGMENT_ACTIVE_BG = "rgba(99, 113, 247, 0.08)";

/** Bot Configuration–style mode picker (Off / Log only / Enforce). */
export function AsecModeSegmented({ value, onChange, options = [], disabled, sx }) {
  return (
    <Box
      sx={{
        ...asecModeSegmentedTrackSx,
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? "none" : "auto",
        ...sx,
      }}
    >
      {options.map((opt) => {
        const on = value === opt.value;
        const palette = ASEC_MODE_TONE_COLORS[opt.tone] || ASEC_MODE_TONE_COLORS.off;
        const textColor = on ? palette.active : palette.idle;
        return (
          <Box
            key={opt.value}
            onClick={() => !disabled && onChange(opt.value)}
            sx={{
              flex: 1,
              p: "8px 10px",
              fontSize: 14,
              fontWeight: on ? 600 : 500,
              borderRadius: "7px",
              color: textColor,
              background: on ? MODE_SEGMENT_ACTIVE_BG : "transparent",
              border: on ? "1px solid rgba(99,113,247,0.22)" : "1px solid transparent",
              boxShadow: on ? "0 1px 2px rgba(99,113,247,0.08)" : "none",
              cursor: disabled ? "not-allowed" : "pointer",
              transition: "all 150ms",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
              whiteSpace: "nowrap",
              "&:hover": on || disabled ? undefined : {
                color: colors.neutral[1000],
                background: "rgba(99, 113, 247, 0.06)",
              },
            }}
          >
            {opt.icon && (
              React.isValidElement(opt.icon)
                ? opt.icon
                : <LayerdIcon icon={opt.icon} size={14} color="inherit" />
            )}
            {opt.label}
          </Box>
        );
      })}
    </Box>
  );
}

/** White bordered card — 8px (table/filter) or 12px (content) radius. */
export function AsecSectionCard({
  title,
  icon,
  right,
  children,
  radius = "8px",
  sx,
  bodySx,
  noPadding,
}) {
  const cardSx = radius === "12px"
    ? { ...asecContentCardSx, ...sx }
    : { ...asecSectionCardSx, ...sx };

  return (
    <Box sx={cardSx}>
      {title && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2.25,
            py: 1.75,
            borderBottom: `1px solid ${colors.neutral[200]}`,
            gap: 1,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            {icon && <LayerdIcon icon={icon} size="sm" color="neutral" />}
            <Typography sx={{ ...asecSectionTitleSx, fontSize: 14 }}>{title}</Typography>
          </Stack>
          {right}
        </Stack>
      )}
      <Box sx={noPadding ? bodySx : { p: 2, ...bodySx }}>{children}</Box>
    </Box>
  );
}

/** Filter bar shell — white card, 8px radius, 36px controls inside. */
export function AsecFilterBar({ children, sx }) {
  return (
    <Box sx={{ ...EVENT_FILTER_BAR_SX, ...sx }}>
      {children}
    </Box>
  );
}

/** Day-over-day delta pill for hero KPI cards — WAF overview pill chrome, % copy. */
export function AsecDeltaSub({ deltaPct, label = "vs. yesterday" }) {
  if (typeof deltaPct !== "number" || !isFinite(deltaPct)) return null;
  const positive = deltaPct >= 0;
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={{
        mt: 1,
        display: "inline-flex",
        alignSelf: "flex-start",
        px: 1,
        py: 0.375,
        borderRadius: "6px",
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1.3,
        fontVariantNumeric: "tabular-nums",
        color: positive ? ACCENT : GREEN,
        backgroundColor: positive ? "rgba(99, 113, 247, 0.08)" : colors.green[10],
        border: `1px solid ${positive ? "rgba(99, 113, 247, 0.18)" : "rgba(31, 193, 107, 0.2)"}`,
      }}
    >
      {positive ? <TrendingUpIcon sx={{ fontSize: 14 }} /> : <TrendingDownIcon sx={{ fontSize: 14 }} />}
      {positive ? "+" : ""}{deltaPct.toFixed(1)}% {label}
    </Stack>
  );
}

function MetricValue({ metric }) {
  const { value, unit, valueSx } = metric;
  const isPrimitive = typeof value === "string" || typeof value === "number";

  if (isPrimitive) {
    return <WAFOverviewStatNumber value={value} unit={unit} sx={valueSx} />;
  }

  return (
    <Box sx={{ mt: 1.25, minHeight: 28, display: "flex", alignItems: "baseline", ...valueSx }}>
      {value}
    </Box>
  );
}

/** Hero KPI value — large primary stat with optional unit suffix and/or status pill. */
export function AsecHeroStatValue({
  primary,
  secondary,
  pillKind,
  pillLabel,
  pill,
  primarySx,
  secondarySx,
  sx,
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={pill || pillLabel ? 1.25 : 0.75}
      flexWrap="wrap"
      sx={sx}
    >
      <Typography component="span" data-overview-metric sx={{ ...wafOverviewStatNumberSx, ...primarySx }}>
        {primary}
      </Typography>
      {secondary != null && secondary !== "" ? (
        <Typography component="span" data-hero-stat-unit sx={{ ...wafOverviewStatUnitSx, ...secondarySx }}>
          {secondary}
        </Typography>
      ) : null}
      {pill || (pillLabel ? <AsecStatusPill kind={pillKind}>{pillLabel}</AsecStatusPill> : null)}
    </Stack>
  );
}

/** Hero KPI mode row — large value label + status pill (matches WAF overview stat size). */
export function AsecHeroModeValue({ label, pillKind, pillLabel }) {
  return <AsecHeroStatValue primary={label} pillKind={pillKind} pillLabel={pillLabel} />;
}

/** KPI metric strip — unified hero row using WAF overview card styling. */
export function AsecMetricStrip({ metrics = [], sx }) {
  if (!metrics.length) return null;
  return (
    <Box sx={{ ...wafOverviewGridSxForCount(metrics.length), ...sx }}>
      {metrics.map((m, i) => (
        <WAFOverviewMetricCard
          key={m.key || i}
          highlight={!!m.highlight}
          sx={{ ...asecOverviewMetricCardSx, ...m.sx }}
          icon={m.icon ? (
            <WAFOverviewIconChip variant="primary">
              <LayerdIcon icon={m.icon} size="sm" color="inherit" />
            </WAFOverviewIconChip>
          ) : null}
          label={m.label}
        >
          <MetricValue metric={m} />
          {m.sub && (
            typeof m.sub === "string" ? (
              <Typography sx={wafOverviewSubSx}>{m.sub}</Typography>
            ) : (
              <Box sx={{ mt: 0.75 }}>{m.sub}</Box>
            )
          )}
        </WAFOverviewMetricCard>
      ))}
    </Box>
  );
}

/** Status / semantic pill — maps design-pack `Pill` kinds to MUI Chip. */
const PILL_KINDS = {
  default: { bg: colors.neutral[100], fg: colors.neutral[700], bd: colors.neutral[200] },
  ok: { bg: "#E8FBEF", fg: "#0f7a42", bd: "#C0EED4" },
  warn: { bg: "#FFF6D0", fg: "#7a5b10", bd: "#FFE89A" },
  danger: { bg: "#FEECEE", fg: "#A11228", bd: "#FBC9CF" },
  info: { bg: colors.primary[100], fg: colors.primary[500], bd: colors.primary[200] },
  indigo: { bg: "rgba(99,113,247,0.10)", fg: colors.primary[500], bd: "rgba(99,113,247,0.24)" },
};

export function AsecStatusPill({ kind = "default", children, sx }) {
  const p = PILL_KINDS[kind] || PILL_KINDS.default;
  return (
    <Chip
      size="small"
      label={children}
      sx={{
        height: 22,
        fontSize: 12,
        fontWeight: 500,
        backgroundColor: p.bg,
        color: p.fg,
        border: `1px solid ${p.bd}`,
        borderRadius: "999px",
        "& .MuiChip-label": { px: 1.125 },
        ...sx,
      }}
    />
  );
}

/** Severity pill — wraps asecSeverityConfig for table/list cells. */
export function AsecSeverityPill({ severity }) {
  const c = asecSeverityConfig(severity);
  return (
    <Chip
      size="small"
      label={c.label}
      sx={{
        px: 1,
        height: 22,
        background: c.bgColor,
        color: c.color,
        borderRadius: "30px",
        fontSize: 12,
        fontWeight: 500,
      }}
    />
  );
}

/** MUI tabs with count badges — replaces native PageTabs. */
export function AsecPageTabs({ value, onChange, tabs = [], right }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
      <Tabs value={value} onChange={onChange} sx={{ ...asecTabsSx, mb: 0, flex: 1 }}>
        {tabs.map((t, i) => (
          <Tab
            key={t.key || i}
            label={
              t.count != null ? `${t.label} (${t.count})` : t.label
            }
          />
        ))}
      </Tabs>
      {right}
    </Stack>
  );
}

/** Inline code snippet — Rubik, generic sm size. */
export function AsecMono({ children, sx }) {
  return (
    <Box
      component="code"
      sx={{
        ...asecTypography.textSmall,
        backgroundColor: colors.neutral[100],
        px: 0.625,
        py: 0.125,
        borderRadius: "4px",
        color: colors.neutral[1000],
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

/** Settings-style table card wrapper (12px, neutral[50] head row). */
export function AsecSettingsTableCard({ children, sx }) {
  return (
    <Box sx={{ ...asecContentCardSx, ...sx }}>
      {children}
    </Box>
  );
}
